import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import toast from "react-hot-toast";
import { requestClubActivation } from "../../services/clubs";
import {
  getActivationQuote,
  getActivationContractPreview,
  acceptActivation,
  downloadClubContract,
  formatMoney,
  type ActivationQuote,
} from "../../services/planActivation";

/**
 * Leaving the trial.
 *
 * The old version of this was a button that opened a support ticket and a
 * message saying somebody would be in touch. This page replaces the somebody.
 *
 * What it will not do is let a club agree to something it has not been shown.
 * The price, the headcount that price is multiplied by, and the part-month it
 * starts from are all on screen before the contract is, and the contract is on
 * screen before the checkbox is worth ticking.
 */
export default function ClubPlanActivation() {
  const { activeClubId, user, refresh } = useAuth();
  const navigate = useNavigate();

  const [quote, setQuote] = useState<ActivationQuote | null>(null);
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [name, setName] = useState(user?.full_name || "");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{ id: string; number: string } | null>(null);
  const [fallbackSent, setFallbackSent] = useState(false);

  const load = useCallback(async () => {
    if (!activeClubId) return;
    setLoading(true);
    try {
      setQuote(await getActivationQuote(activeClubId));
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu am putut încărca detaliile planului.");
    }
    setLoading(false);
  }, [activeClubId]);

  useEffect(() => {
    load();
  }, [load]);

  // The contract is fetched once the quote says there is one to fetch.
  useEffect(() => {
    if (!activeClubId || !quote?.contract_ready || done) return;
    let revoked: string | null = null;
    (async () => {
      try {
        const blob = await getActivationContractPreview(activeClubId);
        const url = URL.createObjectURL(blob);
        revoked = url;
        setPdfUrl(url);
        setPdfError(null);
      } catch {
        setPdfError("Contractul nu a putut fi afișat aici. Poți continua — îl primești pe email la activare.");
      }
    })();
    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [activeClubId, quote?.contract_ready, done]);

  const period = useMemo(() => {
    if (!quote) return null;
    const end = new Date(quote.first_period.ends_at);
    return {
      end: end.toLocaleDateString("ro-RO"),
      days: quote.first_period.billed_days,
      total: quote.first_period.total_days,
    };
  }, [quote]);

  const submit = async () => {
    if (!activeClubId) return;
    setSubmitting(true);
    try {
      const res = await acceptActivation(activeClubId, name.trim());
      if (res.contract) setDone({ id: res.contract.id, number: res.contract.number });
      toast.success("Planul Baza este activ. Contractul îți vine și pe email.");
      // The banner and the write lock both read from here.
      await refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu am putut finaliza activarea.");
      await load();
    }
    setSubmitting(false);
  };

  const download = async () => {
    if (!activeClubId || !done) return;
    try {
      const blob = await downloadClubContract(activeClubId, done.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${done.number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Nu am putut descărca documentul. Îl găsești și în emailul primit.");
    }
  };

  /** When the automatic path is unavailable, reach a person instead of a wall. */
  const askForHelp = async () => {
    if (!activeClubId) return;
    try {
      await requestClubActivation(activeClubId);
      setFallbackSent(true);
      toast.success("Am trimis cererea. Te contactăm în cel mult o zi lucrătoare.");
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu am putut trimite cererea.");
    }
  };

  return (
    <ClubAdminLayout>
      <AdminPageHeader
        title="Activează planul Baza"
        subtitle="Fără card. Citești contractul, îl accepți, și contul continuă neîntrerupt."
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
      />

      {loading && <p className="text-sm text-slate-500">Se încarcă...</p>}

      {!loading && done && (
        <div className="p-6 bg-white border shadow-sm border-emerald-200 rounded-2xl">
          <h2 className="text-lg font-bold text-emerald-700">Gata. Planul Baza este activ.</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            Contractul <strong>{done.number}</strong> a fost emis și ți l-am trimis pe email. Contorizarea
            a început acum — prima factură acoperă doar zilele rămase din luna aceasta.
          </p>
          <div className="flex flex-wrap gap-3 mt-5">
            <button
              type="button"
              onClick={download}
              className="px-4 py-2.5 text-sm font-bold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700"
            >
              Descarcă contractul (PDF)
            </button>
            <button
              type="button"
              onClick={() => navigate("/dashboard/admin-club")}
              className="px-4 py-2.5 text-sm font-semibold bg-white border text-slate-700 border-slate-300 rounded-xl hover:bg-slate-50"
            >
              Înapoi la panou
            </button>
          </div>
        </div>
      )}

      {!loading && !done && quote?.already_active && (
        <div className="p-6 bg-white border shadow-sm border-slate-200 rounded-2xl">
          <h2 className="text-lg font-bold text-slate-800">Clubul este deja pe un plan plătit.</h2>
          <p className="mt-2 text-sm text-slate-600">
            Nu mai e nimic de făcut aici. Facturile le găsești în secțiunea Facturare.
          </p>
        </div>
      )}

      {!loading && !done && quote && !quote.already_active && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
          {/* What it costs */}
          <div className="lg:col-span-2">
            <div className="p-6 bg-white border shadow-sm border-slate-200 rounded-2xl lg:sticky lg:top-6">
              <h2 className="text-base font-bold text-slate-800">Planul {quote.plan_name}</h2>

              <div className="flex items-baseline gap-2 mt-4">
                <span className="text-3xl font-extrabold text-slate-900">
                  {formatMoney(quote.price_per_athlete_cents, quote.currency)}
                </span>
                <span className="text-sm text-slate-500">/ sportiv / lună</span>
              </div>

              <dl className="mt-5 space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-slate-500">Sportivi activi acum</dt>
                  <dd className="font-semibold text-slate-800">{quote.athlete_count}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-slate-500">Estimat pe lună întreagă</dt>
                  <dd className="font-semibold text-slate-800">
                    {formatMoney(quote.monthly_estimate_cents, quote.currency)}
                  </dd>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                  <dt className="text-slate-500">
                    Prima factură
                    {period ? <span className="block text-xs text-slate-400">{period.days} din {period.total} zile, până pe {period.end}</span> : null}
                  </dt>
                  <dd className="text-base font-extrabold text-indigo-700">
                    {formatMoney(quote.first_period.estimate_cents, quote.currency)}
                  </dd>
                </div>
              </dl>

              <p className="mt-5 text-xs leading-relaxed text-slate-500">
                Nu îți cerem cardul. Factura o primești pe email, cu link de plată. Numărul de sportivi
                se recalculează în fiecare lună — plătești pentru cine e în club atunci.
              </p>
            </div>
          </div>

          {/* The contract, and agreeing to it */}
          <div className="space-y-6 lg:col-span-3">
            {quote.missing_legal_fields.length > 0 && (
              <div className="p-5 border bg-amber-50 border-amber-200 rounded-2xl">
                <h3 className="text-sm font-bold text-amber-900">Mai lipsesc câteva date ale clubului</h3>
                <p className="mt-1 text-sm text-amber-800">
                  Contractul nu poate fi scris fără ele: {quote.missing_legal_fields.map((f) => f.label).join(", ")}.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/dashboard/admin-club/settings")}
                  className="mt-3 px-4 py-2 text-sm font-bold text-white bg-amber-600 rounded-xl hover:bg-amber-700"
                >
                  Completează datele clubului
                </button>
              </div>
            )}

            {!quote.contract_ready && (
              <div className="p-5 border bg-slate-50 border-slate-200 rounded-2xl">
                <h3 className="text-sm font-bold text-slate-800">Activarea automată nu e disponibilă acum</h3>
                <p className="mt-1 text-sm text-slate-600">
                  Trimitem cererea ta echipei iGROWth și rezolvăm manual. Nu pierzi nimic între timp.
                </p>
                {fallbackSent ? (
                  <p className="mt-3 text-sm font-semibold text-emerald-700">
                    Cerere trimisă — o urmărești în Suport.
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={askForHelp}
                    className="mt-3 px-4 py-2 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700"
                  >
                    Trimite cererea
                  </button>
                )}
              </div>
            )}

            {quote.contract_ready && (
              <>
                <div className="overflow-hidden bg-white border shadow-sm border-slate-200 rounded-2xl">
                  <div className="px-5 py-4 border-b border-slate-100">
                    <h3 className="text-sm font-bold text-slate-800">Contractul</h3>
                    <p className="mt-0.5 text-xs text-slate-500">
                      Completat cu datele clubului tău. Îl primești și pe email după activare.
                    </p>
                  </div>
                  {pdfError ? (
                    <p className="px-5 py-6 text-sm text-slate-500">{pdfError}</p>
                  ) : pdfUrl ? (
                    <iframe
                      title="Contract de activare"
                      src={pdfUrl}
                      className="w-full h-[520px] bg-slate-50"
                    />
                  ) : (
                    <p className="px-5 py-6 text-sm text-slate-400">Se pregătește contractul...</p>
                  )}
                </div>

                <div className="p-5 bg-white border shadow-sm border-slate-200 rounded-2xl">
                  <label className="block mb-2 text-sm font-medium text-slate-700">
                    Numele complet al reprezentantului legal
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ion Popescu"
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />

                  <label className="flex items-start gap-3 mt-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-1 w-4 h-4 rounded text-indigo-600 border-slate-300 focus:ring-indigo-500"
                    />
                    <span className="text-sm leading-relaxed text-slate-700">
                      Am citit contractul de mai sus, îl accept în numele clubului și sunt împuternicit
                      să o fac.
                    </span>
                  </label>

                  <button
                    type="button"
                    onClick={submit}
                    disabled={!agreed || name.trim().length < 3 || submitting || !quote.can_activate}
                    className="w-full px-4 py-3 mt-5 text-sm font-extrabold text-white transition-colors bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {submitting ? "Se activează..." : "Accept și activez planul Baza"}
                  </button>

                  <p className="mt-3 text-xs text-center text-slate-400">
                    Contorizarea începe din momentul acceptării. Nu se face nicio plată acum.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </ClubAdminLayout>
  );
}
